// Summary of one entry in the logbook.

import { useCallback, useMemo, useState } from 'react';
import { useRootCrumbNavigate } from '../item/RootCrumb';
import Button from 'react-bootstrap/Button';
import { urlForLogbookEntry } from '../../fleet-shared/Urls.mjs';
import { shortDisplayDate } from '../../fleet-shared/Common';
import { Spinner, ErrorText, Confirm, TrashButton } from '../../components';
import { extractErrorText } from '../../fleet-shared/Common.mjs';
import { entryTitle } from '../../fleet-shared/LogbookTypes.mjs';
import { entryWithResolvedEndpoints } from '../../fleet-shared/TripRoute.mjs';

const DeleteButton = ({ showDelete, isDeleting, onDelete, errorText }) => {
    if (!showDelete) {
        return null;
    } else if (isDeleting) {
        return <Spinner />;
    } else {
        return (
            <span>
              <TrashButton onClick={onDelete} ariaLabel='Delete logbook entry' />
              <ErrorText text={errorText} />
            </span>
        );
    }
}


// Single entry
const LogbookEntrySummary = ({entry, item, logbook, placeIndex, showDelete, onDelete}) => {
    const [isDeleting, setIsDeleting] = useState(false);
    const [errorText, setErrorText] = useState(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const navigate = useRootCrumbNavigate();

    // Two titles from one function, and the difference is deliberate. The ROW
    // is a one-line summary, so it takes the gazetteer's short names like every
    // other list. The DELETE CONFIRM does not: it is the last thing shown
    // before a record is destroyed, and "Roche Harbor, San Juan" is exactly the
    // qualifier that tells it from the other Roche Harbor.
    //
    // `placeIndex` arrives as a PROP rather than from usePlaceIndex: this is a
    // presentational row, and a fetch in here would make every test that
    // renders one need Amplify mocks for a string it doesn't assert on. The
    // parent already loads data; it can load this too. Absent, both titles are
    // the full name, which is what they always were.
    // Both read a DISPLAY copy, never `entry` itself: an entry's From/To live
    // in its `fields` blob as a mirror of its route, written when it was last
    // saved, so a place renamed in /me/places since is reachable only through
    // the waypoint's placeId in that route. The handover below still passes the
    // stored entry.
    const displayEntry = useMemo(
        () => entryWithResolvedEndpoints(entry, placeIndex),
        [entry, placeIndex]
    );
    const title = entryTitle(displayEntry, logbook?.logbookType, { placeIndex });
    const fullTitle = entryTitle(displayEntry, logbook?.logbookType);

    const onDetails = useCallback(() => {
        // Pass item + logbook + completedTask via state so the destination page
        // (useSharedItemPageState) can skip a redundant DB fetch.
        navigate(urlForLogbookEntry(item.id, logbook.id, entry.id), {
            state: { item, logbook, completedTask: entry },
        });
    }, [entry, item, logbook, navigate]);

    const onClickDetails = useCallback((e) => {
        e.stopPropagation();
        onDetails();
    }, [onDetails]);

    // User hit delete button. Confirm.
    function handleDelete() {
        setShowConfirm(true);
    }

    // User confirmed delete, show a spinner while deleting.
    async function onConfirmDelete() {
        setIsDeleting(true);

        try {
            await onDelete(entry);
        } catch (err) {
            setErrorText(extractErrorText(err));
        }

        setIsDeleting(false);
        setShowConfirm(false);
    }

    return (
        <div
            className='container logbook-entry-row'
            onClick={onDetails}
            role='button'
            style={{ cursor: 'pointer' }}
        >
            <div className='row pt-1 pb-1 align-items-center'>
                <div className='col-auto entered-data text-nowrap'>
                    {shortDisplayDate(entry.date)}
                </div>
                <div className='col entered-data'>
                    {title}
                </div>
                <div className='col-auto d-flex justify-content-end align-items-center gap-2'>
                    <Button variant='primary' onClick={onClickDetails}>
                      Details
                    </Button>
                    <DeleteButton showDelete={showDelete} isDeleting={isDeleting} onDelete={handleDelete} errorText={errorText} />
                </div>
            </div>

            <Confirm
                show={showConfirm}
                onConfirm={onConfirmDelete}
                onHide = {() => setShowConfirm(false)}
                body='Are you sure?'
                confirmText='Confirm Delete'
                title={`Delete ${fullTitle}`}
            />
        </div>
    );
}

export default LogbookEntrySummary;